import { AutoComplete, AutoCompleteField, Date, DateField, Input, InputField } from '@components/form';
import { ModalButton } from '@components/modalButton/ModalButton';
import { identificationTypeEnum } from '@modules/events/_eventsEnums';
import * as T from '@modules/events/_eventsTypes';
import { externalLookupIdentification } from '@modules/wantedPersons/_wantedPersonsActions';
import { get } from 'lodash';
import Divider from 'material-ui/Divider';
import Search from 'material-ui/svg-icons/action/search';
import * as React from 'react';
import { Col, Grid, Row } from 'react-flexbox-grid';
import { connect } from 'react-redux';
import { compose, withHandlers, withState } from 'recompose';
import { formValueSelector, reduxForm } from 'redux-form';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { ExternalLookupsForm } from './ExternalLookupsForm';

const formatDate = (dateString: string | undefined) => {
  if (!dateString) {
    return 'X';
  }
  const [y, m, d] = dateString.split('-');
  return `${d}.${m}.${y}`;
};

const delictClassificationMap = {
  DELICT: 'Přestupek',
  CRIME: 'Zločin',
  VIOLATION: 'Porušení'
};

const delictViolationTypeMap = {
  NORMAL: 'Normální',
  ALCOHOL: 'Alkohol',
  SPEED: 'Rychlost'
};

const paperStateMap = {
  VALID: 'Platný',
  EXPIRED: 'Platnost vypršela',
  DETAINED: 'Zadržený',
  HANDED_OVER: 'Předán',
  DISCARDED: 'Vyřazený',
  LOST: 'Ztracený',
  STOLEN: 'Ukradený'
};

const DrivingLicenseRow = (props: { name: string; value?: (string | undefined)[]; emp?: boolean }) => {
  const { name, value, emp } = props;
  return (
    <Row style={{ marginTop: 10, marginBottom: 10 }}>
      <Col md={4}>
        {value ? (
          <div style={{ fontWeight: 'bold', color: emp ? 'black' : 'inherit' }}>{name}</div>
        ) : (
          <h3 style={{ color: 'rgba(0, 0, 0, 0.87)' }}>{name}</h3>
        )}
      </Col>
      <Col md={8}>
        {value && (
          <div>
            <>
              {value.map(
                v =>
                  v ? (
                    value.length > 1 ? (
                      <div style={{ display: 'flex', gap: 8 }} key={v}>
                        <div>{value.length > 1 ? '•' : null}</div>
                        <div>{v}</div>
                      </div>
                    ) : (
                      <div key={v}>{v}</div>
                    )
                  ) : null
              )}
            </>
          </div>
        )}
      </Col>
    </Row>
  );
};

const enhance = compose<T.EventsFormProps, T.Outter & T.EventsFormProps>(
  withState('externalLookups', 'handleExternalLookups', []),
  reduxForm({
    form: 'module-events-participants-identifications'
  }),
  connect<any>(state => {
    return {
      value: formValueSelector('module-events-participants-identifications')(state, 'value'),
      paper: get(state, 'form.module-events-participants-identifications.values')
    };
  }),
  withHandlers<any, any>({
    searchExternalLookups: ({ value, handleExternalLookups }) => async () => {
      const response = await externalLookupIdentification({ identificationNumber: value });
      handleExternalLookups(response);
    }
  })
);

const View: React.SFC<T.Outter & T.Inner & T.EventsFormProps> = ({
  handleSubmit,
  closeItem,
  searchExternalLookups,
  externalLookups,
  value,
  paper
}) => {
  const isCrrData = paper && paper.type === 'DRIVING_LICENCE' && paper.driversLicenceDetails;
  const licenceDetails = isCrrData && JSON.parse(paper.driversLicenceDetails);

  return (
    <div>
      <form className="layout" onSubmit={handleSubmit}>
        <Grid fluid={true}>
          <Row>
            <Col className="grid-system-margin" />
          </Row>
          <Row>
            <ModalButton
              icon={<Search />}
              label={'Lustrace dokladů'}
              ModalComponent={ExternalLookupsForm}
              onClick={searchExternalLookups}
              lookupItem={externalLookups}
              disabled={!value}
            />
          </Row>

          <Row>
            <Col md={12}>
              <Row>
                <Col md={4}>
                  <h4>Typ dokladu</h4>
                </Col>
                <Col md={8}>
                  <AutoCompleteField
                    component={AutoComplete}
                    name="type"
                    items={identificationTypeEnum}
                    fullWidth={true}
                    labelFunction={o => get(o, 'label')}
                    valueFunction={o => get(o, 'id')}
                    disabled={isCrrData}
                    arrowstyle={{
                      color: '#e1e0e1',
                      position: 'absolute',
                      right: 0,
                      top: 16
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <h4>Datum zavedení</h4>
                </Col>
                <Col md={8}>
                  <DateField component={Date} name="issuedOn" disabled={isCrrData} />
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <h4>Platnost do</h4>
                </Col>
                <Col md={8}>
                  <DateField component={Date} name="validTo" disabled={isCrrData} allowFuture={true} />
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <h4>Číslo dokladu</h4>
                </Col>
                <Col md={8}>
                  <InputField component={Input} name="value" disabled={isCrrData} />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col className="grid-system-margin" />
          </Row>
        </Grid>

        <Divider />
        {licenceDetails ? (
          <>
            <Grid fluid={true}>
              <Row>
                <Col className="grid-system-margin" />
              </Row>
              <Row>
                <Col md={12}>
                  <DrivingLicenseRow name="Řidičský průkaz" />
                  <DrivingLicenseRow name="Číslo" value={[paper.value]} />
                  <DrivingLicenseRow
                    name="Platnost od - do"
                    value={[`${formatDate(paper.issuedOn)} - ${formatDate(paper.validTo)}`]}
                  />
                  <DrivingLicenseRow name="Stav" value={[paperStateMap[paper.state] || 'neznámý']} />
                  {licenceDetails.groups && licenceDetails.groups.length > 0 && <DrivingLicenseRow name="Skupiny ŘO" />}
                  {licenceDetails.groups &&
                    licenceDetails.groups.map((q, i) => (
                      <>
                        <DrivingLicenseRow
                          name={q.drivingGroup}
                          value={[`${formatDate(q.issueDate)} - ${formatDate(q.expiryDate)}`]}
                          emp={true}
                        />
                        {/* <DrivingLicenseRow
                          name={`Skupina ${q.drivingGroup}`}
                          value={[`${formatDate(q.issueDate)} - ${formatDate(q.expiryDate)}`]}
                        /> */}
                        {/* <DrivingLicenseRow
                          name="Platnost od - do"
                          value={[`${formatDate(q.issueDate)} - ${formatDate(q.expiryDate)}`]}
                        /> */}
                        {q.harmonicCodes &&
                          q.harmonicCodes.length > 0 && (
                            <DrivingLicenseRow
                              name="Harmonizační kódy"
                              value={q.harmonicCodes.map(hc => `${hc.name}: ${hc.description}`)}
                            />
                          )}
                        {q.restrictions &&
                          q.restrictions.map(r => (
                            <DrivingLicenseRow
                              key={r.id}
                              name="Zákaz řízení"
                              value={[
                                r.interruptions && `${formatDate(r.from)} - ${formatDate(r.to)}`,
                                ...(Array.isArray(r.interruptions)
                                  ? r.interruptions.map(
                                      int =>
                                        `Přerušení ${formatDate(int.from)} - ${formatDate(int.to)} (#${
                                          int.proceedingNumber
                                        }): ${int.reason}`
                                    )
                                  : [])
                              ]}
                            />
                          ))}
                        {q.bans &&
                          q.bans.length > 0 && (
                            <DrivingLicenseRow
                              name="Blokace od - do"
                              value={[q.bans && q.bans.map(qb => `${formatDate(qb.from)} - ${formatDate(qb.to)}`)]}
                            />
                          )}
                        {i < licenceDetails.groups.length - 1 ? (
                          <Divider style={{ marginBottom: 12, marginTop: 12 }} />
                        ) : null}
                      </>
                    ))}
                  {licenceDetails.delicts &&
                    licenceDetails.delicts.length > 0 && <DrivingLicenseRow name="Výpis přestupků" />}
                  {licenceDetails.delicts &&
                    licenceDetails.delicts.map(d => (
                      <DrivingLicenseRow
                        key={d.id}
                        name="Přestupek"
                        value={[
                          `Typ: ${delictViolationTypeMap[d.violationType]}`,
                          `Klasifikace: ${delictClassificationMap[d.classification]}`,
                          `Sledován: ${d.watched ? 'ano' : 'ne'}`
                        ]}
                      />
                    ))}
                  {licenceDetails.mentors &&
                    licenceDetails.mentors.length > 0 && <DrivingLicenseRow name="Seznam mentorů" />}
                  {licenceDetails.mentors &&
                    licenceDetails.mentors.map(m => (
                      <DrivingLicenseRow
                        key={m.id}
                        name={m.person.fullName}
                        value={[
                          `Začátek mentorství: ${formatDate(m.mentorshipStart)}`,
                          `Konec mentorství: ${formatDate(m.mentorshipEnd)}`,
                          m.person.birthNumber ? `RČ: ${m.person.birthNumber}` : undefined,
                          m.person.birthDate ? `Narození: ${formatDate(m.person.birthDate)}` : undefined
                        ]}
                      />
                    ))}
                </Col>
              </Row>
              <Row>
                <Col className="grid-system-margin" />
              </Row>
            </Grid>

            <Divider />
          </>
        ) : null}

        {isCrrData ? null : (
          <Button type="submit" label="Uložit a zavřít" primary={true} style={{ margin: '16px 0 16px 16px' }} />
        )}
        <Button label="Zrušit" style={{ margin: '16px 0 16px 16px' }} onClick={closeItem} />
      </form>
    </div>
  );
};

export const EventsFormParticipantsParticipantsIdentificationsForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsFormParticipantsParticipantsIdentificationsForm.tsx